// useSessionPhase.ts — the open session's pod phase and boot log.
//
// Two writers feed the phase, and the last write wins:
//   - the manager socket (`livePhase`), the authority, covering every row;
//   - /api/sessions/{id}/events, an SSE stream for the selected session
//     that reports the same transitions and also tails the sessionenv
//     Pod's boot output — and keeps the page working when the socket
//     cannot connect at all.
// An optimistic flip (suspend, resume) shows at once and is corrected the
// moment the manager disagrees.
//
// The phase decides what sending does — a message typed before the pod is
// up is held until it is — and the boot log renders in the chat pane in
// the meantime, so the wait is legible rather than mysterious. Both reset
// whenever a different session is selected.
//
// Effect order matters here and mirrors the page before the split: the
// livePhase effect is declared first, so on a selection change the events
// effect's reset to 'provisioning' is the write that lands.
import React, { useEffect, useState } from 'react';
import { subscribeSessionEvents } from '../api';
import type { SessionPhase } from './live/sessionLive';
import { appendBootLog } from './sessionRows';
import type { BootLogLine } from './sessionRows';

export interface SessionPhaseState {
  sessionPhase: SessionPhase | null;
  setSessionPhase: React.Dispatch<React.SetStateAction<SessionPhase | null>>;
  bootLogs: BootLogLine[];
  setBootLogs: React.Dispatch<React.SetStateAction<BootLogLine[]>>;
}

export function useSessionPhase(
  selectedId: string | null,
  livePhase: SessionPhase | undefined,
): SessionPhaseState {
  const [sessionPhase, setSessionPhase] = useState<SessionPhase | null>(null);
  const [bootLogs, setBootLogs] = useState<BootLogLine[]>([]);

  useEffect(() => {
    if (livePhase) setSessionPhase(livePhase);
  }, [livePhase]);

  useEffect(() => {
    if (!selectedId) {
      setSessionPhase(null);
      setBootLogs([]);
      return;
    }
    setSessionPhase('provisioning');
    setBootLogs([]);
    const handle = subscribeSessionEvents(selectedId, {
      onPhase: (phase) => setSessionPhase(phase),
      onLog: (line) => setBootLogs((prev) => appendBootLog(prev, line)),
      onError: (msg) => setBootLogs((prev) => [...prev, { text: `events stream error: ${msg}`, count: 1 }]),
    });
    return () => { handle.close(); };
  }, [selectedId]);

  return { sessionPhase, setSessionPhase, bootLogs, setBootLogs };
}
